import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { createApplication, useServerRequest, } from "@aviv-unified-frontend/runtime/lifecycle";
import { useFetcher } from "@aviv-unified-frontend/runtime/fetcher";
import { useLocation } from "@aviv-unified-frontend/runtime/router";
import { createLogger } from "@aviv-unified-frontend/runtime/monitoring";
import { I18nContextProvider } from "@/contexts/i18n";
import { CdpProvider } from "@/contexts/CdpProvider";
import { brandConfiguration } from "@/brands/config";
import { useCdpConfig, useI18n, useDummyFetcher } from "@/hooks";
import { getBffurl, urlParser, getFetcher, getPreviewMode, } from "@/utils";
import { HttpCode, IS_SERVER } from "@/constants";
import { CdpRoot, Head, Loading } from "@/components";
import { datadogRum } from "@datadog/browser-rum-slim";
const env = process.env.CONFIG_ENV;
if (!IS_SERVER && env === "live") {
    datadogRum.init({
        applicationId: "7b1efa64-67ad-4cc0-be56-dc3ba6df27d9",
        clientToken: "puba2d3315e0ff9017c179b8c88354485af",
        site: "datadoghq.eu",
        service: `app_cldp-${process.env.BRAND_ID}`,
        env,
        version: "1.0",
        sessionSampleRate: 5,
        sessionReplaySampleRate: 0,
        trackUserInteractions: false,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
    });
}
const { brand, locale, urlPatterns } = brandConfiguration;
export const App = () => {
    var _a;
    const { pathname, search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const { t, locale, CP47Locale } = useI18n();
    // SSR data is fetched by the Shell using the onServerRequest lifecycle (see https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/tree/main/libraries/runtime/src/lifecycle#useserverrequest)
    // Usually all the data is available in SSR
    const ssrData = (_a = useServerRequest()) === null || _a === void 0 ? void 0 : _a.data;
    // This useFetcher is a Fallback for when SSR data is not available
    // So, by default, we bypass the useFetcher by providing a 'dummy' fetcher which resolves to a null value
    const dummyFetcher = useDummyFetcher();
    const fetcherFallbackRef = useRef(dummyFetcher);
    const hasForceUpdate = useRef(false);
    // By default the useFetcher will get a dummy fetcher and it won't do any actual BFF data-fetching
    const { data: csrData, forceUpdate } = useFetcher("cldp", fetcherFallbackRef.current);
    // If the SSR data is not available, we update the fetcherFallbackRef with an actual fetcher and we force-update the useFetcher
    if (!ssrData && !csrData && !hasForceUpdate.current && !IS_SERVER) {
        const { classifiedId } = urlParser(urlPatterns, pathname);
        const bffUrl = getBffurl({
            env,
            classifiedId,
            brand,
            search: searchParams,
            previewMode: getPreviewMode(pathname),
            isCsr: true,
        });
        fetcherFallbackRef.current = getFetcher(bffUrl, locale);
        hasForceUpdate.current = true;
        forceUpdate({ updateCache: true });
    }
    const { classified = null, error = null } = ssrData || csrData || {};
    const config = useCdpConfig(env, classified);
    const pageProps = {
        config,
        classified,
        error,
    };
    if (process.env.CONFIG_ENV === "dev") {
        console.log(pageProps);
    }
    if (!ssrData && !csrData)
        return _jsx(Loading, { t: t });
    return (_jsx(I18nContextProvider, { value: { t, locale, CP47Locale }, children: _jsxs(CdpProvider, { ...pageProps, children: [_jsx(Head, {}), _jsx(CdpRoot, { ...pageProps })] }) }));
};
// @todo: upload sourceMap https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/tree/main/libraries/runtime/src/monitoring#upload-source-maps
const logger = createLogger({ name: "app_cldp", version: 1 });
// eslint-disable-next-line import/no-default-export
export default createApplication({
    Component: App,
    id: "app_cldp",
    async onServerRequest(request, response) {
        const { classifiedId } = urlParser(urlPatterns, request.url.pathname);
        const env = process.env.CONFIG_ENV;
        const bffUrl = getBffurl({
            env,
            classifiedId,
            brand,
            search: request.url.searchParams,
            previewMode: getPreviewMode(request.url.pathname),
        });
        const xForwardedFor = request === null || request === void 0 ? void 0 : request.getHeader("X-Forwarded-For");
        const fetcher = getFetcher(bffUrl, locale);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore conflic between NextJS and Shell on the xForwardedFor Header
        const { classified = null, error = null } = await fetcher(xForwardedFor);
        if (error === null || error === void 0 ? void 0 : error.code) {
            if (error.code === HttpCode.GONE) {
                response.notFound(410);
            }
            else
                response.notFound();
        }
        return {
            data: {
                classified,
                error,
            },
        };
    },
    logger,
});
